'use client';

import { Box, Button } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  AbgTypography,
  FormError,
  TextFormControl,
  useTranslation,
} from '@abg/kit-base';
import { DIGITS_ONLY_REGEX } from '@abg/shared/regex';

import { useAnalyticsLoginFunnel } from '../../../shared/analytics/hooks';
import { OtpMethod } from '../../../shared/constants/login-form.constants';
import { LoginFormI18n } from '../../../shared/types/LoginFrom.interface';

import { submitMfaFormBodyStyling as styling } from './SubmitMfaFormBody.styling';
import { SubmitMfaFormBodyProps } from './types';

const MIN_CODE_LENGTH = 6;
const MAX_CODE_LENGTH = 9;

type FormValues = {
  confirmationCode: string;
};

export const SubmitMfaFormBody = ({
  partProps = {},
  model,
  error,
  mfaData,
  selectedMethod,
  onStepBack,
  onConfirm,
  onResend,
  onClearServerError,
}: SubmitMfaFormBodyProps) => {
  const t = useTranslation(model.i18n);

  const formMethods = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      confirmationCode: '',
    },
  });

  const { handleSubmit, clearErrors, setValue } = formMethods;

  const recordAnalyticsLoginFunnel = useAnalyticsLoginFunnel();

  useEffect(() => {
    if (error) setValue('confirmationCode', '');
  }, [error, setValue]);

  const handleChooseOtherOptions = () => {
    onStepBack();
    clearErrors();
    setValue('confirmationCode', '');
  };

  const handleResendCode = () => {
    onResend();
    setValue('confirmationCode', '');
  };

  const submitHandler = handleSubmit(
    (data) => {
      recordAnalyticsLoginFunnel({
        method: 'Wizard ID',
        login_state: 'login',
        step_number: 3,
        step_description: 'Confirmation Code',
      });
      onConfirm(data.confirmationCode);
    },
    () => {
      onClearServerError();
    },
  );

  const validationRules = useMemo(() => {
    return getValidationRules(t);
  }, [t]);

  return (
    <FormProvider {...formMethods}>
      <Box
        component="form"
        id="submit-mfa-form"
        sx={styling.container}
        onSubmit={(event) => void submitHandler(event)}
      >
        {!!error && (
          <FormError
            data-testid="mfa-dialog-form-server-error"
            styleParts={partProps.formError?.styleParts}
            config={error}
          />
        )}

        {selectedMethod === OtpMethod.Phone && (
          <AbgTypography
            data-testid="mfa-dialog-phone-method-description"
            sx={styling.chosenMethodDescription}
          >
            {t('mfa.modal.screen.enterCode.phoneNumber.description', {
              phoneNumber: mfaData?.phone,
            })}
          </AbgTypography>
        )}

        {selectedMethod === OtpMethod.Email && (
          <AbgTypography
            data-testid="mfa-dialog-email-method-description"
            sx={styling.chosenMethodDescription}
          >
            {t('mfa.modal.screen.enterCode.email.description', {
              email: mfaData?.email || '',
            })}
          </AbgTypography>
        )}

        <Box sx={styling.entryWrapper}>
          <TextFormControl
            name="confirmationCode"
            testId="confirmation-code"
            required={true}
            label={t('mfa.modal.screen.enterCode.codeField.title')}
            placeholder={t('mfa.modal.screen.enterCode.codeField.placeholder')}
            validationRules={validationRules}
            textFieldProps={{
              slotProps: { htmlInput: { maxLength: MAX_CODE_LENGTH } },
              fullWidth: true,
            }}
            testIdPrefix="mfa-dialog"
          />

          <Box sx={styling.entryOtherActions.wrapper}>
            <AbgTypography
              variant="bodySmallRegular"
              sx={styling.entryOtherActions.otherOptionsText}
            >
              {t('mfa.modal.screen.didNotReceiveCode.label')}
            </AbgTypography>

            <Button className="secondaryText" onClick={handleResendCode}>
              <AbgTypography
                data-testid="mfa-dialog-resend-code-link"
                variant="actionRegularRegular"
              >
                {t('global.resend.label')}
              </AbgTypography>
            </Button>

            <AbgTypography
              variant="bodySmallRegular"
              sx={styling.entryOtherActions.otherOptionsText}
            >
              {t('global.or.label')}
            </AbgTypography>
            <Button
              className="secondaryText"
              onClick={handleChooseOtherOptions}
            >
              <AbgTypography
                data-testid="mfa-dialog-choose-other-options-link"
                variant="actionRegularRegular"
              >
                {t('mfa.modal.screen.enterCode.otherOptions.title')}
              </AbgTypography>
            </Button>
          </Box>
        </Box>
      </Box>
    </FormProvider>
  );
};

const getValidationRules = (
  t: (
    key: keyof LoginFormI18n,
    options?: Record<string, string | number>,
  ) => string,
) => {
  const field = t('mfa.modal.screen.enterCode.codeField.title');

  return {
    required: t('global.field.validation.fieldRequired.message', { field }),
    minLength: {
      value: MIN_CODE_LENGTH,
      message: t('global.field.validation.invalidLength.message', {
        field,
        minLength: MIN_CODE_LENGTH,
        maxLength: MAX_CODE_LENGTH,
      }),
    },
    pattern: {
      value: DIGITS_ONLY_REGEX,
      message: t('global.field.validation.onlyNumbers.message', { field }),
    },
  };
};
